/**
 * Step 3: Business Profile
 * Collects industry and activity information
 */

import React, { useState } from 'react';
import { Briefcase, CaretDown, CaretUp, Check } from '@phosphor-icons/react';
import { MultiSelectChips, StepSection, TextField, InfoCard } from './SharedComponents';

const INDUSTRY_HIERARCHY = {
  financial_services: {
    name: 'Financial Services',
    children: [
      { id: 'banking', name: 'Banking' },
      { id: 'insurance', name: 'Insurance' },
      { id: 'investment_asset_mgmt', name: 'Investment & Asset Management' },
      { id: 'financial_advice', name: 'Financial Advice & Intermediation' },
      { id: 'collective_investments', name: 'Collective Investments' },
      { id: 'retirement_funds', name: 'Retirement Funds & Employee Benefits' },
      { id: 'payments', name: 'Payments & Money Movement' },
      { id: 'credit_lending', name: 'Credit & Lending' },
      { id: 'fintech', name: 'FinTech' },
      { id: 'crypto_digital', name: 'Crypto & Digital Assets' },
      { id: 'capital_markets', name: 'Capital Markets & Securities' },
      { id: 'financial_infrastructure', name: 'Financial Market Infrastructure' },
    ]
  },
  professional_services: {
    name: 'Professional Services',
    children: [
      { id: 'legal_services', name: 'Legal Services' },
      { id: 'accounting_audit', name: 'Accounting & Audit' },
      { id: 'trust_fiduciary', name: 'Trust & Fiduciary Services' },
      { id: 'business_professional', name: 'Business & Professional Services' },
    ]
  },
  technology_communications: {
    name: 'Technology & Communications',
    children: [
      { id: 'technology_software', name: 'Technology & Software' },
      { id: 'telecommunications', name: 'Telecommunications' },
    ]
  },
  property_consumer: {
    name: 'Property & Consumer',
    children: [
      { id: 'property_real_estate', name: 'Property & Real Estate' },
      { id: 'retail_consumer', name: 'Retail & Consumer Goods' },
      { id: 'hospitality_tourism', name: 'Hospitality & Tourism' },
      { id: 'media_entertainment', name: 'Media & Entertainment' },
      { id: 'gambling_gaming', name: 'Gambling & Gaming' },
    ]
  },
  industrial: {
    name: 'Industrial',
    children: [
      { id: 'mining_resources', name: 'Mining & Natural Resources' },
      { id: 'energy_utilities', name: 'Energy & Utilities' },
      { id: 'manufacturing', name: 'Manufacturing' },
      { id: 'construction_engineering', name: 'Construction & Engineering' },
      { id: 'transport_logistics', name: 'Transport & Logistics' },
      { id: 'agriculture', name: 'Agriculture & Agribusiness' },
    ]
  },
  social_public: {
    name: 'Social & Public',
    children: [
      { id: 'healthcare', name: 'Healthcare' },
      { id: 'pharmaceuticals', name: 'Pharmaceuticals & Life Sciences' },
      { id: 'education_training', name: 'Education & Training' },
      { id: 'government_public', name: 'Government & Public Sector' },
      { id: 'state_owned', name: 'State-Owned Entity' },
      { id: 'non_profit', name: 'Non-Profit / NGO / NPC' },
    ]
  }
};

const AURA_ACTIVITY_CATEGORIES = [
  { id: 'legal_practitioner', name: 'Legal practitioner', description: 'Attorneys, advocates and qualifying legal entities' },
  { id: 'trust_company_service', name: 'Trust & company service provider', description: 'Trust/company services' },
  { id: 'estate_property', name: 'Estate/property practitioner', description: 'Property practitioners (Schedule 1)' },
  { id: 'exchange_user', name: 'Authorised user of an exchange', description: 'Exchange authorised users' },
  { id: 'cis_manager', name: 'Collective investment scheme manager', description: 'CIS management' },
  { id: 'bank', name: 'Bank', description: 'Banks (Schedule 1 category)' },
  { id: 'mutual_bank', name: 'Mutual bank', description: 'Mutual banks' },
  { id: 'cooperative_bank', name: 'Co-operative bank', description: 'Co-operative banks' },
  { id: 'long_term_insurer', name: 'Long-term/life insurer', description: 'Life insurance business' },
  { id: 'forex_business', name: 'Foreign exchange business', description: 'Forex activities' },
  { id: 'credit_provider', name: 'Credit provider', description: 'Lending/credit activities' },
  { id: 'fsp', name: 'Financial services provider', description: 'FAIS financial advice/intermediary' },
  { id: 'money_transfer', name: 'Money/value transfer provider', description: 'Domestic/cross-border transfers' },
  { id: 'high_value_goods', name: 'High-value goods dealer', description: 'Precious metals, stones, art' },
  { id: 'gambling', name: 'Gambling institution', description: 'Gambling activities' },
  { id: 'casp', name: 'Crypto asset service provider', description: 'CASP activities' },
];

const BUSINESS_ACTIVITIES = [
  { id: 'financial_advice', name: 'Financial advice' },
  { id: 'intermediary_services', name: 'Intermediary services' },
  { id: 'discretionary_investment', name: 'Discretionary investment management' },
  { id: 'fund_administration', name: 'Fund administration' },
  { id: 'insurance_underwriting', name: 'Insurance underwriting' },
  { id: 'claims_administration', name: 'Claims administration' },
  { id: 'deposit_taking', name: 'Deposit taking' },
  { id: 'payments', name: 'Payments' },
  { id: 'money_transfer', name: 'Money/value transfer' },
  { id: 'crypto_services', name: 'Crypto-asset services' },
  { id: 'credit', name: 'Credit' },
  { id: 'asset_custody', name: 'Asset custody' },
  { id: 'cis_management', name: 'Collective investment management' },
  { id: 'retirement_admin', name: 'Retirement fund administration' },
];

// Industry Selector with hierarchy
const IndustrySelector = ({ selected, onChange }) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set(['financial_services']));
  
  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };
  
  const toggleIndustry = (industryId) => {
    if (selected.includes(industryId)) {
      onChange(selected.filter(s => s !== industryId));
    } else {
      onChange([...selected, industryId]);
    }
  };
  
  return (
    <div className="space-y-2" data-testid="industry-selector">
      <label className="text-sm font-medium text-text-secondary">Industries</label>
      <div className="border border-border rounded-lg overflow-hidden">
        {Object.entries(INDUSTRY_HIERARCHY).map(([categoryId, category]) => (
          <div key={categoryId}>
            <button
              type="button"
              onClick={() => toggleCategory(categoryId)}
              className="w-full px-4 py-3 bg-background-surface flex items-center justify-between text-left hover:bg-background transition-colors border-b border-border"
            >
              <span className="text-sm font-medium text-text-primary">{category.name}</span>
              {expandedCategories.has(categoryId) ? 
                <CaretUp size={18} className="text-text-tertiary" /> : 
                <CaretDown size={18} className="text-text-tertiary" />
              }
            </button>
            
            {expandedCategories.has(categoryId) && (
              <div className="p-3 bg-background grid grid-cols-2 md:grid-cols-3 gap-2">
                {category.children.map(industry => {
                  const isSelected = selected.includes(industry.id);
                  return (
                    <button
                      key={industry.id}
                      type="button"
                      onClick={() => toggleIndustry(industry.id)}
                      className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${
                        isSelected 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-background-surface border border-border text-text-secondary hover:border-primary/50'
                      }`}
                      data-testid={`industry-${industry.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {isSelected && <Check size={14} weight="bold" />}
                        <span className="truncate">{industry.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map(id => {
            const industry = Object.values(INDUSTRY_HIERARCHY)
              .flatMap(cat => cat.children)
              .find(ind => ind.id === id);
            return industry ? (
              <span key={id} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                {industry.name}
              </span>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
};

// AURA Activity Category Selector with descriptions
const AuraActivitySelector = ({ selected, onChange }) => {
  const toggleActivity = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };
  
  return (
    <div className="space-y-2" data-testid="aura-activity-selector">
      <label className="text-sm font-medium text-text-secondary">
        AURA Activity Categories (FIC Schedule 1 aligned)
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {AURA_ACTIVITY_CATEGORIES.map(activity => {
          const isSelected = selected.includes(activity.id);
          return (
            <button
              key={activity.id}
              type="button"
              onClick={() => toggleActivity(activity.id)}
              className={`p-3 rounded-lg text-left transition-all ${
                isSelected 
                  ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background' 
                  : 'bg-background-surface border border-border hover:border-primary/50'
              }`}
              data-testid={`aura-activity-${activity.id}`}
            >
              <div className="flex items-start gap-2">
                <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-primary-foreground/20 border-primary-foreground/50' : 'border-border'
                }`}>
                  {isSelected && <Check size={12} weight="bold" />}
                </div>
                <div>
                  <span className={`text-sm font-medium ${isSelected ? 'text-primary-foreground' : 'text-text-primary'}`}>
                    {activity.name}
                  </span>
                  <p className={`text-xs mt-0.5 ${isSelected ? 'text-primary-foreground/80' : 'text-text-tertiary'}`}>
                    {activity.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Step3BusinessProfile = ({ formData, updateField }) => {
  return (
    <StepSection
      title="Business Profile"
      description="Define your industry and business activities"
      icon={Briefcase}
    >
      {/* Industry Selection */}
      <IndustrySelector
        selected={formData.industries || []}
        onChange={(v) => updateField('industries', v)}
      />
      
      {/* AURA Activity Categories */}
      <AuraActivitySelector
        selected={formData.aura_activity_categories || []}
        onChange={(v) => updateField('aura_activity_categories', v)}
      />
      
      {/* Business Activities */}
      <MultiSelectChips
        label="Business Activities"
        options={BUSINESS_ACTIVITIES}
        selected={formData.business_activities || []}
        onChange={(v) => updateField('business_activities', v)}
        testId="business-activities"
      />
      
      {/* Other Activity */}
      {formData.aura_activity_categories?.includes('other') && (
        <TextField
          label="Other Activity (please specify)"
          value={formData.other_activity_description}
          onChange={(v) => updateField('other_activity_description', v)}
          placeholder="Describe your other business activity..."
          testId="other-activity-input"
        />
      )}
      
      {/* Info about regulatory implications */}
      {(formData.aura_activity_categories?.length > 0 || formData.business_activities?.length > 0) && (
        <InfoCard variant="info" title="Regulatory Implications">
          Based on your selected activities, AURA will identify applicable legislation including 
          FICA requirements, FAIS obligations, and industry-specific regulations.
        </InfoCard>
      )}
    </StepSection>
  );
};

export default Step3BusinessProfile;
